/**
 * モデル・工数プリセット選択UI（3段ラジオ + 折りたたみ詳細）。
 * 既定=普通（CLIデフォルト。後方互換）。
 * Zustand v5 無限再レンダー対策: このコンポーネントは外部 state を直接持たず、
 * props で受け取った値と onChange を使う純粋制御コンポーネント。
 */
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  AgentPreset,
  AgentProvider,
  CLAUDE_EFFORTS,
  CLAUDE_MODELS,
  CODEX_EFFORTS,
  PRESET_LABELS,
} from "../../lib/tauri";

interface PresetPickerProps {
  provider: AgentProvider;
  preset: AgentPreset;
  modelOverride: string;
  effortOverride: string;
  onPresetChange: (p: AgentPreset) => void;
  onModelOverrideChange: (m: string) => void;
  onEffortOverrideChange: (e: string) => void;
  /** ダークUI向けかどうか（CommandSheet / AgentBoard は暗め） */
  dark?: boolean;
}

const PRESETS: AgentPreset[] = ["light", "normal", "full"];

const PRESET_DESCRIPTIONS: Record<AgentPreset, string> = {
  light: "速い・安い（haiku / low）",
  normal: "デフォルト（変更なし）",
  full: "深い・丁寧（opus / high）",
};

export function PresetPicker({
  provider,
  preset,
  modelOverride,
  effortOverride,
  onPresetChange,
  onModelOverrideChange,
  onEffortOverrideChange,
  dark = false,
}: PresetPickerProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const baseText = dark ? "text-zinc-200" : "text-text";
  const mutedText = dark ? "text-zinc-400" : "text-muted";
  const borderClass = dark ? "border-white/15" : "border-border dark:border-border-dark";
  const bgSelected = dark ? "bg-indigo-400/20 border-indigo-400/60 text-indigo-100" : "bg-brand-500/10 border-brand-500 text-brand-700 dark:text-brand-200";
  const bgUnselected = dark
    ? "bg-black/20 border-white/10 text-zinc-300 hover:bg-black/40"
    : "bg-bg border-border text-muted hover:bg-hover dark:bg-bg-dark dark:border-border-dark dark:text-muted-dark dark:hover:bg-hover-dark";

  const effortOptions = provider === "claude" ? CLAUDE_EFFORTS : CODEX_EFFORTS;

  return (
    <div className="space-y-2">
      {/* 3段プリセット */}
      <div className="flex gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPresetChange(p)}
            title={PRESET_DESCRIPTIONS[p]}
            className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition ${
              preset === p ? bgSelected : bgUnselected
            }`}
          >
            {PRESET_LABELS[p]}
          </button>
        ))}
      </div>

      {/* 折りたたみ詳細選択 */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className={`inline-flex items-center gap-1 text-[10px] ${mutedText} hover:opacity-80`}
      >
        <ChevronDown
          className={`h-3 w-3 transition ${showAdvanced ? "rotate-180" : ""}`}
        />
        詳細設定（上級者向け）
      </button>

      {showAdvanced && (
        <div className={`flex flex-wrap gap-2 rounded-md border ${borderClass} p-2`}>
          {/* claude のみモデル選択 */}
          {provider === "claude" && (
            <div className="flex items-center gap-1.5">
              <label className={`text-[10px] ${mutedText}`}>モデル</label>
              <select
                value={modelOverride}
                onChange={(e) => onModelOverrideChange(e.target.value)}
                className={`rounded border ${borderClass} bg-transparent px-1.5 py-0.5 text-[11px] ${baseText}`}
              >
                <option value="">プリセット任せ</option>
                {CLAUDE_MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* effort 選択 */}
          <div className="flex items-center gap-1.5">
            <label className={`text-[10px] ${mutedText}`}>工数</label>
            <select
              value={effortOverride}
              onChange={(e) => onEffortOverrideChange(e.target.value)}
              className={`rounded border ${borderClass} bg-transparent px-1.5 py-0.5 text-[11px] ${baseText}`}
            >
              <option value="">プリセット任せ</option>
              {effortOptions.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
